/** Inbox column + filter model. Filters live in the URL so views are shareable links. */
import type { CaseListFilters } from './api';

/**
 * Una columna de la bandeja. La tabla no hace scroll horizontal: las celdas envuelven y las columnas con `secondary`
 * muestran otro dato del caso en una segunda línea (el tipo bajo el radicado, el área bajo el asunto); al exportar
 * esa segunda línea va como columna propia. `oneLine` evita el salto en fechas y etiquetas; `lines` recorta el texto libre.
 */
export interface ColumnDef { key: string; header: string; width?: string; sortField?: 'createdAt' | 'dueAt' | 'priority' | 'radication'; secondary?: { key: string; header: string }; oneLine?: boolean; lines?: 1 | 2 | 3 }
export const COLUMNS: ColumnDef[] = [
  { key: 'radication', header: 'Radicado', width: '9.5rem', sortField: 'radication', oneLine: true, secondary: { key: 'caseTypeName', header: 'Tipo' } },
  { key: 'createdAt', header: 'Fecha', sortField: 'createdAt', oneLine: true },
  { key: 'citizenName', header: 'Ciudadano' },
  { key: 'subject', header: 'Tema / Asunto', lines: 2, secondary: { key: 'organizationalUnitName', header: 'Área' } },
  { key: 'specialtyName', header: 'Especialidad' },
  { key: 'priority', header: 'Prioridad', sortField: 'priority', oneLine: true },
  { key: 'due', header: 'Vencimiento', sortField: 'dueAt', oneLine: true },
  { key: 'assigneeName', header: 'Responsable' },
  { key: 'status', header: 'Estado', oneLine: true },
  { key: 'channel', header: 'Canal', oneLine: true },
  { key: 'courtName', header: 'Juzgado' },
  { key: 'judicialRadication', header: 'Radicado judicial' },
  // Solo con el correo saliente por Datacrédito Postal (la bandeja la oculta en los demás casos).
  { key: 'rastreo', header: 'Rastreo', oneLine: true },
];
export const DEFAULT_COLUMNS = ['radication', 'createdAt', 'citizenName', 'subject', 'priority', 'due', 'assigneeName', 'status'];

export const OPEN_STATUSES = ['RECEIVED', 'PROCESSING', 'PENDING_ASSIGNMENT', 'IN_PROGRESS', 'WAITING_INFORMATION', 'WAITING_APPROVAL', 'WAITING_SIGNATURE', 'ESCALATED'];
export const ALL_STATUSES = [...OPEN_STATUSES, 'TRANSFERRED', 'RESOLVED', 'CLOSED', 'ARCHIVED'];
export const QUICK_VIEWS: Record<string, { label: string; apply: (f: InboxFilters) => InboxFilters }> = {
  pendientes: { label: 'Pendientes', apply: (f) => ({ ...f, status: OPEN_STATUSES }) },
  mias: { label: 'Mías', apply: (f) => ({ ...f, mine: true }) },
  vencidas: { label: 'Vencidas', apply: (f) => ({ ...f, overdue: true }) },
  revisar: { label: 'Por revisar', apply: (f) => ({ ...f, requiresReview: true }) },
};

/** Client-side-only filters (the API has no params for these yet) are kept next to the server ones. */
export interface InboxFilters extends CaseListFilters { from?: string; to?: string; view?: string }

const ARRAY_KEYS = new Set(['status']);
const BOOL_KEYS = new Set(['overdue', 'requiresReview', 'mine', 'important', 'favorites']);
export function parseFilters(sp: URLSearchParams): InboxFilters {
  const f: Record<string, unknown> = {};
  for (const key of ['q', 'channel', 'priority', 'assigneeId', 'specialtyId', 'organizationalUnitId', 'caseTypeCode', 'courtId', 'sort', 'dir', 'from', 'to', 'view']) { const v = sp.get(key); if (v) f[key] = v; }
  for (const key of ARRAY_KEYS) { const v = sp.getAll(key); if (v.length) f[key] = v; }
  for (const key of BOOL_KEYS) { if (sp.get(key) === 'true') f[key] = true; }
  let out = f as InboxFilters;
  if (out.view && QUICK_VIEWS[out.view]) out = QUICK_VIEWS[out.view]!.apply(out);
  return out;
}
export function toSearchParams(f: InboxFilters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
    else sp.set(k, String(v));
  }
  return sp;
}
/** Server-side params only (strip client-only keys). */
export function serverFilters(f: InboxFilters): CaseListFilters {
  const { from: _f, to: _t, view: _v, ...rest } = f;
  // La bandeja es de trabajo: sin filtro de estado muestra solo casos activos. El histórico se ve eligiendo los estados en el filtro «Estado».
  if (!rest.status?.length) rest.status = OPEN_STATUSES;
  return rest;
}
export function countActiveFilters(f: InboxFilters): number {
  return ['status', 'channel', 'priority', 'assigneeId', 'specialtyId', 'organizationalUnitId', 'caseTypeCode', 'courtId', 'overdue', 'requiresReview', 'mine', 'important', 'favorites', 'from', 'to'].filter((k) => { const v = (f as Record<string, unknown>)[k]; return Array.isArray(v) ? v.length > 0 : !!v; }).length;
}

export interface SavedView { id: string; name: string; params: string; columns: string[]; createdAt: string }
